import { useNavigate } from 'react-router-dom';
import TimeFilters from '../../../../components/time-filters';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../../services/api.service';
import { AppStore } from '../../../../store/store';
import { UserState } from '../../../../store/user.slice';
import StatisticSection from '../../../../components/statistics-section';
import StatisticsSectionBarChartType from '../../../../components/statistics-section.bar-chart-type';
import StatisticsSectionChartType from '../../../../components/statistics-section.chart-type';
import './reports-status-page.scss';
import { colorsPalette } from '../../../../utils/colors-utils';
import AppSelect from '../../../../components/form/base/app-select';
import { StatisticLineChartData, StatusType } from '../../../../types/statistics.types';
import { getColorByCardBrandType, transformCardBrandToChartLineData, transformToChartLineData } from '../../../../utils/constants';
import StatisticsSectionLineChart from '../../../../components/statistics-section.line-chart';


const ReportsStatusPage: React.FC = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();
    const [transactionsNumber, setTransactionsNumber] = useState();

    const [cardTypes, setCardTypes] = useState([]);
    const [cardsLoader, setCardsLoader] = useState(false);
    const [cardChartTypes, setCardChartTypes] = useState<StatisticLineChartData[]>();
    const [cardChartLoader, setCardChartLoader] = useState(false);

    const [processors, setProcessors] = useState();
    const [processorsLoader, setProcessorsLoader] = useState(false);
    const [processorsChart, setProcessorsChart] = useState<StatisticLineChartData[]>();
    const [processorsChartLoader, setProcessorsChartLoader] = useState(false);

    const [merchants, setMerchants] = useState();
    const [merchantsLoader, setMerchantsLoader] = useState(false);
    const [merchantsChart, setMerchantsChart] = useState<StatisticLineChartData[]>();
    const [merchantsChartLoader, setMerchantsChartLoader] = useState(false);


    const [selectedStatus, setSelectedStatus] = useState<StatusType>();
    const { user } = useSelector<AppStore, UserState>((state) => state.user)
    const statusOptions = Object.values(StatusType).map((status) => ({
        label: status,
        value: status,
    }));

    const isMerchant = (user?.roles?.includes("merchant") || user?.roles?.includes("bas_merchant_view")) ?? false;


    useEffect(() => {
        apiService.getTransactionsSummary({ startDate, endDate, status: selectedStatus })
            .then((response) => { setTransactionsNumber(response.data.transactionCount) })
            .catch((error) => console.error('Error fetching transactions number:', error));

        setCardsLoader(true)
        apiService.getCardTypeStatistics({ startDate, endDate, status: selectedStatus })
            .then((response) => {
                const transformedData = response.data.map((item: any) => ({
                    color: getColorByCardBrandType(item.name),
                    name: item.name,
                    quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                    quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                    qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                    qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                }));
                setCardTypes(transformedData);
            })
            .catch((error) => console.error('Error fetching card types:', error))
            .finally(() => setCardsLoader(false))

        setCardChartLoader(true)
        apiService.getChartCardData({status: selectedStatus })
            .then((response) => {
                const cardsChartData: StatisticLineChartData[] = transformCardBrandToChartLineData(response.data);
                setCardChartTypes(cardsChartData);
            })
            .catch((error) => console.error('Error fetching card chart data:', error))
            .finally(() => setCardChartLoader(false))

        setProcessorsLoader(true)
        apiService.getProcessorStatistics({ startDate, endDate, status: selectedStatus })
            .then((response) => {
                const transformedData = response.data.map((item: any, index: number) => {
                    return ({
                        color: colorsPalette[index],
                        name: item.name,
                        type: isMerchant ? null : item.type,
                        quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                        quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                        qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                        qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                    })
                });
                setProcessors(transformedData);
            })
            .catch((error) => console.error('Error fetching processors:', error))
            .finally(() => setProcessorsLoader(false))

        setProcessorsChartLoader(true)
        apiService.getChartProcessorData({status: selectedStatus })
            .then((response) => {
                const processorChartData: StatisticLineChartData[] = transformToChartLineData(response.data);
                setProcessorsChart(processorChartData);
            })
            .catch((error) => console.error('Error fetching processor chart data:', error))
            .finally(() => setProcessorsChartLoader(false))

        if (!isMerchant && user) {
            setMerchantsLoader(true)
            apiService.getMerchantStatistics({ startDate, endDate, status: selectedStatus })
                .then((response) => {
                    const transformedData = response.data.map((item: any, index: number) => ({
                        id: item.id,
                        color: colorsPalette[index],
                        name: item.name,
                        quantitativePercent: item.quantityAndQuality?.quantitativePercent || 0,
                        quantitativeValue: item.quantityAndQuality?.quantitativeValue || 0,
                        qualitativePercent: item.quantityAndQuality?.qualitativePercent || 0,
                        qualitativeValue: item.quantityAndQuality?.qualitativeValue || 0,
                    }));
                    setMerchants(transformedData);
                })
                .catch((error) => console.error('Error fetching merchants:', error))
                .finally(() => setMerchantsLoader(false))


            setMerchantsChartLoader(true)
            apiService.getChartMerchantData({status: selectedStatus })
                .then((response) => {
                    const merchantsChartData: StatisticLineChartData[] = transformToChartLineData(response.data);
                    setMerchantsChart(merchantsChartData);
                })
                .catch((error) => console.error('Error fetching merchant chart data:', error))
                .finally(() => setMerchantsChartLoader(false))
        }

    }, [user?.id, startDate, endDate, selectedStatus]);

    const handleMerchantNavigate = (id: string) => {
        navigate(`/app/merchants/merchant/${id}`);
    }


    const handleDataUpdate = (startDate?: string, endDate?: string) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }

    return (
        <div className='reports-status-page page-content'>
            <div className="page-header flex-row flex-gap-20 flex-align-start-center width-100 flex-wrap">
                <div className='page-title'>
                    Reports Status
                </div>
                <AppSelect
                    theme='filter'
                    value={selectedStatus}
                    options={statusOptions}
                    placeholder='Select Status'
                    onChange={(value) => setSelectedStatus(value)}
                />
            </div>
            <div>
                <TimeFilters transactionsNo={transactionsNumber} handleFilters={handleDataUpdate} />
            </div>

            <StatisticSection title='Card Brand Statistics'>
                <StatisticsSectionChartType data={cardTypes || []} isLoading={cardsLoader} />
                <StatisticsSectionLineChart data={cardChartTypes || []} isLoading={cardChartLoader} />
            </StatisticSection>
            <StatisticSection title='Processors Statistics'>
                <StatisticsSectionBarChartType data={processors || []} isLoading={processorsLoader} />
                <StatisticsSectionLineChart data={processorsChart || []} isLoading={processorsChartLoader} />
            </StatisticSection>
            {!isMerchant &&
                <StatisticSection title='Merchants Statistics'>
                    <StatisticsSectionBarChartType data={merchants || []} onNavigate={handleMerchantNavigate} isLoading={merchantsLoader} />
                    <StatisticsSectionLineChart data={merchantsChart || []} isLoading={merchantsChartLoader} />
                </StatisticSection>
            }
        </div>
    )
}

export default ReportsStatusPage;